import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  FAVORITED_PROVIDERS_PAGE_ENTRY_POINTS,
  FavoritedProvidersEntrypointNavProps,
  PAGE_ROUTES,
  S4C_HEADER_EVENTS,
} from '@lyrahealth-inc/shared-app-logic'

import { useGetSearchForCareEventProps } from '../../../../common/hooks/useGetSearchForCareEventProps'
import { trackOnboardEvent } from '../../../../data/mixpanel'
import { AppDispatch } from '../../../../data/storeConfiguration/store'
import {
  getIsFavoritedProvidersFeatureAvailable,
  getUserHasPastFavoritedProviders,
} from '../../../../data/user/userSelectors'
import { getOnboardHasFavoritedProviders } from '../../data/onboardSelectors'

/**
 * A hook that provides the necessary props for displaying and handling
 * the favorited providers star icon in navigation components across web.
 */
export const useFavoritedProvidersEntrypointNav = (): FavoritedProvidersEntrypointNavProps => {
  const dispatch = useDispatch<AppDispatch>()
  const isFavoritedProvidersFeatureAvailable = useSelector(getIsFavoritedProvidersFeatureAvailable)
  const userHasPastFavoritedProviders = useSelector(getUserHasPastFavoritedProviders)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const getSearchForCareEventProps = useGetSearchForCareEventProps()

  // Determine if we should show the star icon in the nav bar, based on feature flag and override. Hide for international users
  const shouldShowFavoritedProvidersStarIcon =
    (isFavoritedProvidersFeatureAvailable && userHasPastFavoritedProviders) || false

  const userHasFavoritedProviders = useSelector(getOnboardHasFavoritedProviders)

  const onHoverForIconExplanation = useCallback(() => {
    dispatch(
      trackOnboardEvent({
        ...getSearchForCareEventProps(),
        event: S4C_HEADER_EVENTS.HOVER_FOR_ICON_EXPLANATION,
        iconText: 'Favorite providers',
      }),
    )
  }, [dispatch, getSearchForCareEventProps])

  const onFavoritedProvidersStarIconPressed = useCallback(
    ({ isFromHamburgerMenu = false }: { isFromHamburgerMenu?: boolean }) => {
      if (!shouldShowFavoritedProvidersStarIcon || !isFavoritedProvidersFeatureAvailable) {
        return
      }
      return navigate(PAGE_ROUTES.FAVORITED_PROVIDERS, {
        state: {
          from: pathname,
          entryPoint: isFromHamburgerMenu
            ? FAVORITED_PROVIDERS_PAGE_ENTRY_POINTS.HAMBURGER_MENU
            : FAVORITED_PROVIDERS_PAGE_ENTRY_POINTS.NAV_BAR,
        },
      })
    },
    [pathname, isFavoritedProvidersFeatureAvailable, navigate, shouldShowFavoritedProvidersStarIcon],
  )

  return {
    isFavoritedProvidersStarIconFilled: userHasFavoritedProviders,
    shouldShowFavoritedProvidersStarIcon,
    onFavoritedProvidersStarIconPressed,
    onHoverForIconExplanation,
  }
}
